import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ApiClient } from "@/services/api";
import { Card, Button, Alert, LoadingSpinner } from "@/components/shared";

interface VerifyResult {
  status: string;
  subscription?: {
    tier: string;
    lead_limit: number;
    regions: string[];
  };
}

export function PaymentCallback({ redirectTo = "/agent/subscription" }: { redirectTo?: string }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reference = searchParams.get("reference") || searchParams.get("trxref");

  useEffect(() => {
    if (!reference) {
      setStatus("error");
      setError("No payment reference found");
      return;
    }

    const verify = async () => {
      try {
        const api = new ApiClient();
        const data = await api.get<VerifyResult>(`/subscriptions/verify?reference=${encodeURIComponent(reference)}`);
        setResult(data);
        setStatus("success");
        // Redirect after 3 seconds
        setTimeout(() => navigate(redirectTo, { replace: true }), 3000);
      } catch (err: any) {
        setStatus("error");
        setError(err.message || "Payment verification failed");
      }
    };

    verify();
  }, [reference, navigate, redirectTo]);

  if (status === "loading") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <LoadingSpinner />
        <p className="text-white/70">Verifying your payment...</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="max-w-md mx-auto px-4 py-8">
        <Card>
          <Alert type="error">{error}</Alert>
          <div className="mt-4 flex gap-3">
            <Button variant="primary" onClick={() => window.location.reload()}>
              Retry
            </Button>
            <Button variant="secondary" onClick={() => navigate(redirectTo, { replace: true })}>
              Back to Subscription
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-8">
      <Card>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Payment Successful!</h3>
          <p className="text-white/70 mb-4">
            Your {result?.subscription?.tier} subscription is now active.
          </p>
          <p className="text-sm text-white/50">Redirecting in 3 seconds...</p>
        </div>
      </Card>
    </div>
  );
}
